import { Flex, Menu, Text, useMantineTheme } from '@mantine/core';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon';
import { useTranslationCapTable } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import {
	CapMenu,
	StyledCapMenuItem,
} from '@dealstack/frontend/app/pages/Project/Cap/CapTablePage/common/CapMenu.tsx';
import {
	CAP_TABLE_VALUE_DISPLAY_MODE,
	CAP_TABLE_VALUES_HELD_TYPE,
} from '@dealstack/frontend/app/pages/Project/Cap/CapTablePage/common/tableSettings.ts';
import { useContext } from 'react';
import { CapChartPageContext } from '@dealstack/frontend/app/pages/Project/Cap/CapChartPage/CapChartPage.tsx';
import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds.ts';
import { AdvancedSwitch } from '@dealstack/frontend/shared/components/Switch/AdvancedSwitch.tsx';

export const CapChartSettings = () => {
	const tCapTable = useTranslationCapTable();
	const theme = useMantineTheme();

	const capChartContext = useContext(CapChartPageContext);
	const { onValuesHeldTypeChange, onSummarizedChange, onValueDisplayModeChange, chartSettings } =
		capChartContext.capChartData;

	const { onToggleSpecifySecurities, inSecuritiesMode, refetchDataWithResetSelectedSecurities } =
		capChartContext.capChartSecurities;

	const showPercentages =
		chartSettings.valueDisplayMode === CAP_TABLE_VALUE_DISPLAY_MODE.PERCENTAGE;

	const handleSpecifySecuritiesClick = () => {
		onToggleSpecifySecurities(!inSecuritiesMode);

		if (inSecuritiesMode) {
			refetchDataWithResetSelectedSecurities();
		}
	};

	const handleValueDisplayModeChange = (value: CAP_TABLE_VALUE_DISPLAY_MODE) => {
		if (value === CAP_TABLE_VALUE_DISPLAY_MODE.ABSOLUTE && inSecuritiesMode) {
			onToggleSpecifySecurities(false);
			refetchDataWithResetSelectedSecurities();
		}
		onValueDisplayModeChange?.(value);
	};

	return (
		<CapMenu
			buttonTargetIcon={<Icon variant="more-v3" size="vlg" />}
			dataTestId={TEST_ELEMENT_IDS.GSC_SETTINGS_BUTTON}
		>
			<Menu.Label p="0.5rem" fw={400} c="gray.9">
				Customise View
			</Menu.Label>
			<Flex gap=".25rem" direction="column">
				<Menu.Item
					p="0.5rem"
					closeMenuOnClick={false}
					data-testid={TEST_ELEMENT_IDS.GSC_SETTINGS_DETAIL_ITEM}
				>
					<AdvancedSwitch
						selectedValue={chartSettings?.showSummarized}
						twoWay={true}
						leftValue={false}
						rightValue={true}
						leftText={tCapTable('header_actions.switch.full_table')}
						rightText={tCapTable('header_actions.switch.summary')}
						onChange={onSummarizedChange}
						justify="space-between"
						selectedColor={theme.colors.dark[8]}
						unselectedColor={theme.colors.dark[8]}
					/>
				</Menu.Item>

				<Menu.Item p="0.5rem" closeMenuOnClick={false}>
					<AdvancedSwitch
						selectedValue={chartSettings?.valuesHeldType}
						twoWay={true}
						leftValue={CAP_TABLE_VALUES_HELD_TYPE.SECURITIES}
						rightValue={CAP_TABLE_VALUES_HELD_TYPE.VOTES}
						leftText={tCapTable('header_actions.switch.securities')}
						rightText={tCapTable('header_actions.switch.votes')}
						onChange={onValuesHeldTypeChange}
						justify="space-between"
						selectedColor={theme.colors.dark[8]}
						unselectedColor={theme.colors.dark[8]}
					/>
				</Menu.Item>

				<Menu.Item p="0.5rem" closeMenuOnClick={false}>
					<AdvancedSwitch
						selectedValue={chartSettings?.valueDisplayMode}
						leftValue={CAP_TABLE_VALUE_DISPLAY_MODE.ABSOLUTE}
						rightValue={CAP_TABLE_VALUE_DISPLAY_MODE.PERCENTAGE}
						leftText={tCapTable('header_actions.switch.absolute')}
						rightText={tCapTable('header_actions.switch.percentage')}
						onChange={handleValueDisplayModeChange}
						twoWay={true}
						justify="space-between"
						selectedColor={theme.colors.dark[8]}
						unselectedColor={theme.colors.dark[8]}
					/>
				</Menu.Item>
				<Menu.Divider />
				<StyledCapMenuItem
					p=".5rem"
					leftSection={<Icon variant="table-v3" size="lg" />}
					onClick={handleSpecifySecuritiesClick}
					disabled={!showPercentages}
					className={inSecuritiesMode ? 'active' : ''}
					closeMenuOnClick={false}
				>
					<Text size="B6Medium">{tCapTable('header_actions.specify_securities')}</Text>
				</StyledCapMenuItem>
			</Flex>
		</CapMenu>
	);
};
